/*
 * Timeline Controller
*/

import Event from '../events';
import EventHandler from '../event-handler';
import Cea608Parser from '../utils/cea-608-parser';
import WebVTTParser from '../utils/webvtt-parser';
import { logger } from '../utils/logger';

function clearCurrentCues(track) {
	if (track && track.cues) {
		while (track.cues.length > 0) {
			track.removeCue(track.cues[0]);
		}
	}
}

function reuseVttTextTrack(inUseTrack, manifestTrack) {
	return inUseTrack && inUseTrack.label === manifestTrack.name && !(inUseTrack.textTrack1 || inUseTrack.textTrack2);
}

function intersection(x1, x2, y1, y2) {
	return Math.min(x2, y2) - Math.max(x1, y1);
}

class TimelineController extends EventHandler {

	constructor(hls) {
		super(hls, Event.MEDIA_ATTACHING,
			Event.MEDIA_ATTACHED, //Mactrix line added, temp fix for caption loss on backwards seek
			Event.MEDIA_DETACHING,
			Event.FRAG_PARSING_USERDATA,
			Event.MANIFEST_LOADING,
			Event.MANIFEST_LOADED,
			Event.FRAG_LOADED,
			Event.LEVEL_SWITCHING,
			Event.TEXT_TRACK_CHANGED, // mactrix addition: add TEXT_TRACK_CHANGED
			Event.INIT_PTS_FOUND
		);

		this.hls = hls;
		this.config = hls.config;
		this.enabled = true;
		this.Cues = hls.config.cueHandler;
		this.textTracks = [];
		this.tracks = [];
		this.unparsedVttFrags = [];
		this.initPTS = undefined;
		this.cueRanges = [];
		this.preFlushed = false;

		this.captionsOnSeekWorkaround = this.hls.browIE || this.hls.browEdge; //Mactrix line added, temp fix for caption loss on backwards seek

		if (this.config.enableCEA708Captions) {
			var self = this;
			var sendAddTrackEvent = function (track, media) {
				var e = null;
				try {
					e = new window.Event('addtrack');
				}
				catch (err) {
					//for IE11
					e = document.createEvent('Event');
					e.initEvent('addtrack', false, false);
				}
				e.track = track;
				media.dispatchEvent(e);
			};

			var channel1 =
				{
					newCue: function (startTime, endTime, screen) {
						if (!self.textTrack1) {
			//Enable reuse of existing text track.
							var existingTrack1 = self.getExistingTrack('1');
							if (!existingTrack1) {
								const textTrack1 = self.createTextTrack(
									'captions',
									self.config.captionsTextTrack1Label,
									self.config.captionsTextTrack1LanguageCode
								);
								if (textTrack1) {
									textTrack1.textTrack1 = true;
									self.textTrack1 = textTrack1;
								}
							}
							else {
								self.textTrack1 = existingTrack1;
								clearCurrentCues(self.textTrack1);

								sendAddTrackEvent(self.textTrack1, self.media);
							}

							//mactrix insertion: Needed to track text track additions
							//we can't put this event in sendAddTrackEvent as that only runs if reusing an existing track
							self.hls.trigger(Event.TEXT_TRACK_ADDED, { track : self.textTrack1 } );
							// end mactrix change

						}
						self.addCues('textTrack1', startTime, endTime, screen);
					},
				};

			var channel2 =
				{
					newCue: function (startTime, endTime, screen) {
						if (!self.textTrack2) {
			//Enable reuse of existing text track.
							var existingTrack2 = self.getExistingTrack('2');
							if (!existingTrack2) {
								const textTrack2 = self.createTextTrack(
									'captions',
									self.config.captionsTextTrack2Label,
									self.config.captionsTextTrack1LanguageCode
								);
								if (textTrack2) {
									textTrack2.textTrack2 = true;
									self.textTrack2 = textTrack2;
								}
							}
							else {
								self.textTrack2 = existingTrack2;
								clearCurrentCues(self.textTrack2);

								sendAddTrackEvent(self.textTrack2, self.media);
							}

							//mactrix insertion: Needed to track text track additions
							self.hls.trigger(Event.TEXT_TRACK_ADDED, { track : self.textTrack2 } );

						}
						self.addCues('textTrack2', startTime, endTime, screen);
					},
				};

			this.cea608Parser = new Cea608Parser(0, channel1, channel2);
		}
	}

	addCues(channel, startTime, endTime, screen) {
	//Mactrix additions start
	//at certain parts of the frag load/parse process, seeking will cause the player to decide to just finish parsing the currently loading frag anyway
	//if we're seeking backwards, this adds future caption cues, causing errors in IE/Edge when we try to add current cues
	//as such, we need to do a check and, if needed, clear before adding cues
	//the buffer flush done as part of the backwards seek can't be done here, as the timing would cause an infinte frag loading loop
		if(this.captionsOnSeekWorkaround && this.media.textTracks.length) {
			if(this.media.textTracks[0].cues.length) {
				if(this.media.textTracks[0].cues[this.media.textTracks[0].cues.length - 1].endTime > startTime) {
					this.clearAllCues();
				}
			}
		}
		//Mactrix additions end
		// skip cues which overlap more than 50% with previously parsed time ranges
		const ranges = this.cueRanges;
		let merged = false;
		for (let i = ranges.length; i--;) {
			let cueRange = ranges[i];
			let overlap = intersection(cueRange[0], cueRange[1], startTime, endTime);
			if (overlap >= 0) {
				cueRange[0] = Math.min(cueRange[0], startTime);
				cueRange[1] = Math.max(cueRange[1], endTime);
				merged = true;
				if ((overlap / (endTime - startTime)) > 0.5) {
					return;
				}
			}
		}
		if (!merged) {
			ranges.push([startTime, endTime]);
		}
		this.Cues.newCue(this[channel], startTime, endTime, screen);
	}

  // Triggered when an initial PTS is found; used for synchronisation of WebVTT.
	onInitPtsFound(data) {
		if (typeof this.initPTS === 'undefined') {
			this.initPTS = data.initPTS;
		}

	// Due to asynchrony, initial PTS may arrive later than the first VTT fragments are loaded.
	// Parse any unparsed fragments upon receiving the initial PTS.
		if (this.unparsedVttFrags.length) {
			this.unparsedVttFrags.forEach(frag => {
				this.onFragLoaded(frag);
			});
			this.unparsedVttFrags = [];
		}
	}

  //Mactix addition start
  //clears cues in both text tracks
  //we did this in several places so making a separate func for it
	clearAllCues() {
		clearCurrentCues(this.textTrack1);
		clearCurrentCues(this.textTrack2);
		this.cueRanges = [];
	}
  //Mactrix addition end

	getExistingTrack(channelNumber) {
		const media = this.media;
		if (media) {
			for (let i = 0; i < media.textTracks.length; i++) {
				let textTrack = media.textTracks[i];
				let propName = 'textTrack' + channelNumber;
				if (textTrack[propName] === true) {
					return textTrack;
				}
			}
		}
		return null;
	}

	createTextTrack(kind, label, lang) {
		const media = this.media;
		if (media) {
			return media.addTextTrack(kind, label, lang);
		}
		return null; //Mactrix line added
	}

	destroy() {
		this.clearAllCues(); //Mactrix line added, clear text tracks
		EventHandler.prototype.destroy.call(this);
	//Mactrix additions start
	//memory clean up
		this.Cues = null;
		this.cea608Parser = null;
		this.config = null;
		this.cueRanges = null;
		this.onvseeking = null;
		if(this.textTrack1) {
			this.textTrack1 = null;
		}
		if(this.textTrack2) {
			this.textTrack2 = null;
		}
		this.textTracks = null;
		this.tracks = null;
		this.unparsedVttFrags = null;
		this.vttCCs = null;
		this.hls = null;
	//Mactrix additions end
	}

	onMediaAttaching(data) {
		this.media = data.media;
	}

  /*
  onMediaDetaching() {
	clearCurrentCues(this.textTrack1);
	clearCurrentCues(this.textTrack2);
  }
  */

  //Mactrix changes and additions start
  //Temporary fix for loss of captions when seeking backwards on IE/Edge
  //Clearing out the caption cues stops the error that was causing us to lose captions completely
  //It's not a perfect solution however, as sometimes active subtitle cues get stuck on screen for a while when we do this
	onMediaAttached() {
		this.onvseeking = this.onMediaSeeking.bind(this);
		if(this.captionsOnSeekWorkaround) {
			this.media.addEventListener('seeking', this.onvseeking);
		}

		if (this.hls.browTizen2 && !this.textTrack1) {
			const textTrack1 = this.createTextTrack(
				'captions',
				this.config.captionsTextTrack1Label,
				this.config.captionsTextTrack1LanguageCode
			);
			if (textTrack1) {
				textTrack1.textTrack1 = true;
				this.textTrack1 = textTrack1;

				this.hls.trigger(Event.TEXT_TRACK_ADDED, { track : this.textTrack1 } );
			}
		}
	}

	onMediaDetaching() {
		if(this.media && this.captionsOnSeekWorkaround) {
			this.media.removeEventListener('seeking', this.onvseeking);
		}
		clearCurrentCues(this.textTrack1);
		clearCurrentCues(this.textTrack2);
	}

	onMediaSeeking() {
		let media = this.media;
		let levelController = this.hls.levelController;
		let preFlushed = this.preFlushed;
		this.preFlushed = false;

		if(media) {
			if(media.textTracks.length) {
				if(media.textTracks[0].cues.length) {
					if(media.textTracks[0].cues[media.textTracks[0].cues.length - 1].endTime > media.currentTime &&
						levelController.chunkIDAtTime(media.textTracks[0].cues[media.textTracks[0].cues.length - 1].endTime) !==
						levelController.chunkIDAtTime(media.currentTime)
					) { //we don't need to flush if the two compared times are in the same frag
						if (!preFlushed) {
							this.hls.trigger(Event.BUFFER_FLUSHING, { startOffset: 0, endOffset:Number.POSITIVE_INFINITY }); //add negative buffer?
						}
						this.clearAllCues();
					}
				}
			}
		}
	}
  //Mactrix changes and additions end

	onManifestLoading() {
		this.lastSn = -1; // Detect discontiguity in fragment parsing
		this.prevCC = -1;
		this.vttCCs = { ccOffset: 0, presentationOffset: 0 }; // Detect discontinuity in subtitle manifests

	// clear outdated subtitles
		const media = this.media;
		if (media) {
			const textTracks = media.textTracks;
			if (textTracks) {
				for (let i = 0; i < textTracks.length; i++) {
					clearCurrentCues(textTracks[i]);
				}
			}
		}
	}

	onManifestLoaded(data) {
		this.textTracks = [];
		this.unparsedVttFrags = this.unparsedVttFrags || [];
		this.initPTS = undefined;
		this.cueRanges = [];

		if (this.config.enableWebVTT) {
			this.tracks = data.subtitles || [];
			const inUseTracks = this.media ? this.media.textTracks : [];

			this.tracks.forEach((track, index) => {
				let textTrack;
				const inUseTrack = inUseTracks[index];
		// Reuse tracks with the same label, but do not reuse 608/708 tracks
				if (reuseVttTextTrack(inUseTrack, track)) {
					textTrack = inUseTrack;
				}
				else {
					textTrack = this.createTextTrack('subtitles', track.name, track.lang);
				}
				if(textTrack) { //Mactrix line added
					textTrack.mode = track.default ? 'showing' : 'hidden';
					this.textTracks.push(textTrack);
				} //Mactrix line added
			});
		}
	}

	onLevelSwitching() {
		this.enabled = this.hls.currentLevel.closedCaptions !== 'NONE';
	}

	onFragLoaded(data) {
		let frag = data.frag;
		let payload = data.payload;

		if (frag.type === 'main') {
			var sn = frag.sn;
			// if this frag isn't contiguous, clear the parser so cues with bad start/end times aren't added to the textTrack
			if (sn !== this.lastSn + 1) {
				this.cea608Parser.reset();
			}
			this.lastSn = sn;
		}
	// If fragment is subtitle type, parse as WebVTT.
		else if (frag.type === 'subtitle') {
			if (payload.byteLength) {
		// We need an initial synchronisation PTS. Store fragments as long as none has arrived.
				if (typeof this.initPTS === 'undefined') {
					this.unparsedVttFrags.push(data);
					return;
				}
				let vttCCs = this.vttCCs;
				if (!vttCCs[frag.cc]) {
					vttCCs[frag.cc] = { start: frag.start, prevCC: this.prevCC, new: true };
					this.prevCC = frag.cc;
				}
				let textTracks = this.textTracks;
				const hls = this.hls;

		// Parse the WebVTT file contents.
				WebVTTParser.parse(payload, this.initPTS, vttCCs, frag.cc, function (cues) {
			// Add cues and trigger event with success true.
					cues.forEach(cue => {
						textTracks[frag.trackId].addCue(cue);
					});
					hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, { success: true, frag: frag });
				},
				function (e) {
					// Something went wrong while parsing. Trigger event with success false.
					logger.log(`Failed to parse VTT cue: ${e}`);
					hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, { success: false, frag: frag });
				});
			}
			else {
		// In case there is no payload, finish unsuccessfully.
				this.hls.trigger(Event.SUBTITLE_FRAG_PROCESSED, { success: false, frag: frag });
			}
		}
	}

  //Mactrix addition start
	onTextTrackChanged(/*data*/) {
		this.clearAllCues();
	}
  //Mactrix addition end

	onFragParsingUserdata(data) {
	// push all of the CEA-708 messages into the interpreter
	// immediately. It will create the proper timestamps based on our PTS value
		if (this.enabled && this.config.enableCEA708Captions) {
			for (var i = 0; i < data.samples.length; i++) {
				var ccdatas = this.extractCea608Data(data.samples[i].bytes);
				this.cea608Parser.addData(data.samples[i].pts, ccdatas);
			}
		}
	}

	extractCea608Data(byteArray) {
		let count = byteArray[0] & 31;
		let position = 2;

		let tmpByte;
		let ccbyte1;
		let ccbyte2;
		let ccValid;
		let ccType;
		let actualCCBytes = [];

		for (let j = 0; j < count; j++) {
			tmpByte = byteArray[position++];
			ccbyte1 = 0x7F & byteArray[position++];
			ccbyte2 = 0x7F & byteArray[position++];
			ccValid = (4 & tmpByte) !== 0;
			ccType = 3 & tmpByte;

			if (ccbyte1 === 0 && ccbyte2 === 0) {
				continue;
			}

			if (ccValid) {
				if (ccType === 0) /*|| ccType === 1*/ {
					actualCCBytes.push(ccbyte1);
					actualCCBytes.push(ccbyte2);
				}
			}
		}
		return actualCCBytes;
	}
}

export default TimelineController;



// WEBPACK FOOTER //
// ./src/hlsjs/controller/timeline-controller.js